/**
 * Skills service — frontend API client for the agent-skills feature.
 * Mirrors the routes mounted under /api/skills and /api/agents/:id/skills
 * in apps/server/src/routes/skills.ts.
 */

import { api } from "@/utils/api-client";

/**
 * Skill row shape returned from the API. Matches `SkillRecord` on the
 * server but typed locally so the client doesn't import from `@taqat/db`.
 *
 * `metadata.seedAssets` and `metadata.categories` are recent additions —
 * old skills authored before they existed simply omit them, so consumers
 * should default to an empty array when reading.
 */
export type Skill = {
  id: string;
  scope: "platform" | "workspace";
  workspaceId: string | null;
  slug: string;
  name: string;
  description: string;
  instructions: string;
  metadata: {
    icon?: string;
    color?: string;
    tags?: string[];
    seedAssets?: string[];
    categories?: string[];
  };
  /** Null for platform skills seeded by migration — they have no author. */
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

/**
 * How an asset gets composed into the model's prompt. Mirrors the server
 * enum in `@taqat/db/skill-asset.ts`. `template-substitute` is still Phase 3 —
 * accepted by the API but not yet rendered, so the workbench surfaces it as a
 * disabled option. `reference-only` IS rendered (inlined under a REFERENCE
 * heading, outside the inline budget).
 */
export type SkillAssetRenderMode =
  | "inline-full"
  | "inline-preview"
  | "template-substitute"
  | "reference-only";

/**
 * Skill asset row. Matches the server's `SkillAssetRecord` shape.
 * `sizeBytes` is computed on the server; the workbench uses it for the
 * inline-budget indicator without recomputing.
 */
export type SkillAsset = {
  id: string;
  skillId: string;
  path: string;
  content: string;
  mimeType: string | null;
  renderMode: SkillAssetRenderMode;
  previewLines: number | null;
  sizeBytes: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

/**
 * Payload for creating or replacing a skill asset. Path lives in the
 * URL; everything else is in the body.
 */
export type UpsertSkillAssetPayload = {
  content: string;
  mimeType?: string;
  renderMode: SkillAssetRenderMode;
  previewLines?: number;
};

/**
 * Partial update payload — content, mode, mime, or preview lines may
 * change independently. Switching out of `inline-preview` mode clears
 * `previewLines` server-side automatically.
 */
export type UpdateSkillAssetPayload = Partial<{
  content: string;
  mimeType: string;
  renderMode: SkillAssetRenderMode;
  previewLines: number | null;
}>;

/**
 * Payload for creating a workspace skill via the API.
 * Scope and workspaceId are inferred server-side from the auth context.
 */
export type CreateSkillPayload = {
  slug: string;
  name: string;
  description: string;
  instructions: string;
  metadata?: { icon?: string; color?: string; tags?: string[] };
};

/**
 * Payload for partial skill updates. Slug and scope are immutable in v1.
 */
export type UpdateSkillPayload = Partial<
  Pick<CreateSkillPayload, "name" | "description" | "instructions" | "metadata">
>;

/**
 * Body for the rename endpoint. The original path lives in the URL.
 */
export type RenameSkillAssetPayload = { toPath: string };

type SuccessEnvelope<T> = { success: true; data: T };

/**
 * Static-method service grouping all skill-related API calls.
 * Mirrors the existing KnowledgeService shape so the codebase stays consistent.
 */
export class SkillsService {
  /**
   * Lists active workspace skills, or platform-catalog skills when
   * `scope === 'platform'` is passed.
   */
  static async list(
    scope?: "workspace" | "platform",
  ): Promise<SuccessEnvelope<{ skills: Skill[] }>> {
    const url = scope === "platform" ? "/skills?scope=platform" : "/skills";
    return api.get(url, {
      requiresAuth: true,
      showErrorToast: true,
    }) as Promise<SuccessEnvelope<{ skills: Skill[] }>>;
  }

  /**
   * Fetches a single skill by id.
   */
  static async get(
    id: string,
  ): Promise<SuccessEnvelope<{ skill: Skill }>> {
    return api.get(`/skills/${id}`, {
      requiresAuth: true,
      showErrorToast: true,
    }) as Promise<SuccessEnvelope<{ skill: Skill }>>;
  }

  /**
   * Creates a new workspace-scoped skill.
   */
  static async create(
    payload: CreateSkillPayload,
  ): Promise<SuccessEnvelope<{ skill: Skill }>> {
    return api.post("/skills", payload, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: true,
    }) as Promise<SuccessEnvelope<{ skill: Skill }>>;
  }

  /**
   * Applies a partial update to a workspace skill.
   */
  static async update(
    id: string,
    payload: UpdateSkillPayload,
  ): Promise<SuccessEnvelope<{ skill: Skill }>> {
    return api.patch(`/skills/${id}`, payload, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: true,
    }) as Promise<SuccessEnvelope<{ skill: Skill }>>;
  }

  /**
   * Soft-deletes a workspace skill.
   */
  static async remove(id: string): Promise<void> {
    await api.delete(`/skills/${id}`, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: true,
    });
  }

  /**
   * Lists skills currently attached to an agent.
   */
  static async listForAgent(
    agentId: string,
  ): Promise<SuccessEnvelope<{ skills: Skill[] }>> {
    return api.get(`/agents/${agentId}/skills`, {
      requiresAuth: true,
      showErrorToast: true,
    }) as Promise<SuccessEnvelope<{ skills: Skill[] }>>;
  }

  /**
   * Attaches a skill to an agent. Caller shows the success toast so the
   * message can be specific ("Skill attached"), not the generic API-level fallback.
   */
  static async attach(agentId: string, skillId: string): Promise<void> {
    await api.post(
      `/agents/${agentId}/skills`,
      { skillId },
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      },
    );
  }

  /**
   * Detaches a skill from an agent. Caller shows the success toast so the
   * message can be specific ("Skill detached"), not the generic API-level fallback.
   */
  static async detach(agentId: string, skillId: string): Promise<void> {
    await api.delete(`/agents/${agentId}/skills/${skillId}`, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    });
  }

  // ── Skill assets ──────────────────────────────────────────────────────
  //
  // Workbench-only endpoints. Asset paths are URL-encoded in the path
  // segment so authors can use folder-style names like "templates/hero.html"
  // without bumping into Hono's segment matcher.

  /**
   * Lists every live asset attached to a skill. Returns metadata only —
   * `content` is filled in for each row so the workbench's prompt-preview
   * pane can render without a second round-trip per file.
   */
  static async listAssets(
    skillId: string,
  ): Promise<SuccessEnvelope<{ assets: SkillAsset[] }>> {
    return api.get(`/skills/${skillId}/assets`, {
      requiresAuth: true,
      showErrorToast: true,
    }) as Promise<SuccessEnvelope<{ assets: SkillAsset[] }>>;
  }

  /**
   * Creates or replaces an asset at the given path. Re-saving the same
   * path is idempotent on the server (unique constraint upserts).
   */
  static async upsertAsset(
    skillId: string,
    path: string,
    payload: UpsertSkillAssetPayload,
  ): Promise<SuccessEnvelope<{ asset: SkillAsset }>> {
    return api.put(
      `/skills/${skillId}/assets/${encodeURIComponent(path)}`,
      payload,
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      },
    ) as Promise<SuccessEnvelope<{ asset: SkillAsset }>>;
  }

  /**
   * Applies a partial update — content, mime, render mode, or preview
   * lines. Use `renameAsset` for path changes; PATCH cannot rename.
   */
  static async updateAsset(
    skillId: string,
    path: string,
    payload: UpdateSkillAssetPayload,
  ): Promise<SuccessEnvelope<{ asset: SkillAsset }>> {
    return api.patch(
      `/skills/${skillId}/assets/${encodeURIComponent(path)}`,
      payload,
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      },
    ) as Promise<SuccessEnvelope<{ asset: SkillAsset }>>;
  }

  /**
   * Renames an asset. Server runs insert + soft-delete in a transaction
   * so the operation is atomic — a partial failure can't leave two
   * paths pointing at the same content.
   */
  static async renameAsset(
    skillId: string,
    fromPath: string,
    payload: RenameSkillAssetPayload,
  ): Promise<SuccessEnvelope<{ asset: SkillAsset }>> {
    return api.post(
      `/skills/${skillId}/assets/${encodeURIComponent(fromPath)}/rename`,
      payload,
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      },
    ) as Promise<SuccessEnvelope<{ asset: SkillAsset }>>;
  }

  /**
   * Soft-deletes an asset. Idempotent — a no-op on missing rows still
   * returns 204 so the workbench can retry without worrying about state.
   */
  static async deleteAsset(skillId: string, path: string): Promise<void> {
    await api.delete(
      `/skills/${skillId}/assets/${encodeURIComponent(path)}`,
      {
        requiresAuth: true,
        showErrorToast: true,
        showSuccessToast: false,
      },
    );
  }
}
